import { useRef } from 'react';
import { Link } from 'react-router-dom';
import { categories } from '@/mocks/categories';
import { articles } from '@/mocks/articles';

export default function CategoryChannels() {
  const scrollRef = useRef<HTMLDivElement>(null);

  const scroll = (direction: 'left' | 'right') => {
    if (scrollRef.current) {
      const amount = 320;
      scrollRef.current.scrollBy({
        left: direction === 'left' ? -amount : amount,
        behavior: 'smooth',
      });
    }
  };

  return (
    <section className="py-14 md:py-20 bg-background-100">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="flex items-end justify-between mb-10 md:mb-12">
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground-950">
            Explore by Topic
          </h2>
          <div className="hidden md:flex items-center gap-2">
            <button
              onClick={() => scroll('left')}
              className="w-9 h-9 rounded-full bg-background-200 hover:bg-background-300 flex items-center justify-center transition-colors cursor-pointer"
              aria-label="Scroll left"
            >
              <i className="ri-arrow-left-s-line text-foreground-700"></i>
            </button>
            <button
              onClick={() => scroll('right')}
              className="w-9 h-9 rounded-full bg-background-200 hover:bg-background-300 flex items-center justify-center transition-colors cursor-pointer"
              aria-label="Scroll right"
            >
              <i className="ri-arrow-right-s-line text-foreground-700"></i>
            </button>
          </div>
        </div>

        <div
          ref={scrollRef}
          className="flex gap-4 md:gap-5 overflow-x-auto pb-4 scrollbar-hide"
          style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
        >
          {categories.map((cat) => {
            const catArticles = articles.filter(a => a.category === cat.slug).slice(0, 3);
            return (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className="flex-shrink-0 w-[260px] md:w-[300px] bg-background-50 rounded-lg overflow-hidden group cursor-pointer"
              >
                <div className="px-4 md:px-5 pt-4 md:pt-5 pb-2">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-secondary-100 text-secondary-700">
                      <i className={`${cat.icon} text-sm`}></i>
                    </span>
                    <span className="font-heading text-base md:text-lg font-semibold text-foreground-950">
                      {cat.name}
                    </span>
                  </div>
                  <p className="text-xs text-foreground-500 leading-relaxed mb-3">
                    {cat.description}
                  </p>
                </div>

                <div className="px-4 md:px-5 pb-4 md:pb-5">
                  {catArticles.map((article, idx) => (
                    <div
                      key={article.slug}
                      className={`flex items-start gap-3 py-2.5 ${
                        idx < catArticles.length - 1 ? 'border-b border-background-200/70' : ''
                      }`}
                    >
                      <div className="flex-shrink-0 w-14 h-14 md:w-16 md:h-16 rounded-md overflow-hidden">
                        <img
                          src={article.imageUrl}
                          alt={article.title}
                          className="w-full h-full object-cover object-top"
                          title={`${article.title}`}
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs md:text-sm font-medium text-foreground-800 leading-snug line-clamp-2 group-hover:text-primary-600 transition-colors">
                          {article.title}
                        </p>
                        <span className="text-[10px] md:text-xs text-foreground-400 mt-1 block">
                          {article.readTime}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="px-4 md:px-5 pb-4 md:pb-5">
                  <span className="text-xs font-label font-medium text-primary-600 hover:text-primary-700 transition-colors">
                    View all {cat.count} articles <i className="ri-arrow-right-line ml-1"></i>
                  </span>
                </div>
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
}